import { createAsyncThunk } from '@reduxjs/toolkit';
import Cryptr from 'cryptr';
import axios from 'axios';
import { AXIOS_RESERVATION } from '../../util/axiosInterceptors';
import { setBookingData, setGuestInfo, resetReservationsAggregation, setExpectedReservations } from './costSummarySlice';
import { config } from '../../config';

/**
 * Step 1 - Tokenization (only for new cards)
 */
export const makeTokenization = createAsyncThunk(
  'costSummary/makeTokenization',
  async ({ values, uniqueDate }, { getState, dispatch, rejectWithValue }) => {
    try {
      console.log('makeTokenization>>>>>>>');
      const {
        costSummary, signIn, stripe,
      } = getState();
      const { totalCost } = costSummary;
      const { userData } = signIn;
      const { hotelDetails } = stripe;
      const {
        firstName,
        lastName,
        email,
        countryCodeForMobileNumber,
        mobileNoWithoutCountryCode,
        phoneNo,
        name,
        card,
        expiryDate,
        cvv,
        queryDetails,
      } = values;

      const cryptr = new Cryptr(hotelDetails?.secretKey);

      dispatch(
        setGuestInfo({
          firstName,
          lastName,
          email,
          countryCodeForMobileNumber,
          mobileNoWithoutCountryCode,
          phoneNo,
        }),
      );

      const hotelId = hotelDetails?.hotelCode;
      const dataForTokenization = {
        name,
        card,
        expiry_date: expiryDate,
        cvv,
        email,
      };
      const encrypted = cryptr.encrypt(JSON.stringify(dataForTokenization));

      const res = await AXIOS_RESERVATION.post(
        `v1/cardTokenization/${hotelId}/web/enc`,
        { card: encrypted },
      );

      if (res?.data?.success) {
        const decrypted = JSON.parse(cryptr.decrypt(res.data.result.data));
        console.log(decrypted, 'decrypted');
        const tokenizedData = {
          card,
          currency: 'usd',
          customer: '',
          description: 'Reservation',
          encryptedSource: res.data.result.data,
          email: userData?.email || email,
          isPrefered: true,
          amount: parseFloat(totalCost),
          queryDetails,
        };

        // 🔹 Send tokenizedData into aggregator
        dispatch(makeAllReservations({tokenizedData,uniqueDate}));

        return {
          tokenizedData,
          status: {
            apiMessage: res?.data?.result?.message,
            apiSuccess: res?.data?.success,
          },
        };
      }
      return {
        tokenizedData: {},
        status: {
          apiMessage: res?.data?.result?.message,
          apiSuccess: res?.data?.success,
        },
      };
    } catch (error) {
      throw rejectWithValue({
        status: {
          apiSuccess: error?.response?.data?.success || false,
          apiMessage:
            error?.response?.data?.result?.message
            || 'Something went wrong please try again later',
        },
      });
    }
  },
);

/**
 * Step 2 - Aggregator for multi-room flow
 */
export const makeAllReservations = createAsyncThunk(
  'costSummary/makeAllReservations',
  async ({tokenizedData, uniqueDate}, { getState, dispatch, rejectWithValue }) => {
    try {
      const { room } = getState();
      const { roomCounts } = room;

      // 🔹 initialize aggregation
      dispatch(resetReservationsAggregation());
      dispatch(setExpectedReservations(roomCounts.length));

      // 🔹 Run payment for each room
      for (let i = 0; i < roomCounts.length; i++) {
        await dispatch(makePayment({ tokenizedData, index: i, uniqueDate }));
      }

      return {
        status: {
          apiMessage: 'All reservations attempted',
          apiSuccess: true,
        },
      };
    } catch (error) {
      return rejectWithValue({
        status: {
          apiMessage: error?.message || 'Reservations failed',
          apiSuccess: false,
        },
      });
    }
  },
);

/**
 * Step 3 - Payment for single room
 */
export const makePayment = createAsyncThunk(
  'costSummary/makePayment',
  async ({ tokenizedData, index, uniqueDate }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { calender, room, stripe } = getState();
      const { arrivalDate, departureDate } = calender;
      const { hotelDetails } = stripe;
      const { ratePlanCode, corpCode, selectedRooms, roomCounts, allOptions2 } = room;

      const { queryDetails } = tokenizedData;

      // prevent mutation across loop calls
      const dataCopy = { ...tokenizedData };
      delete dataCopy.queryDetails;

      const IpAddress = await axios.get('https://api.ipify.org?format=json');
      const { userAgent } = window.navigator;
      const userOs = userAgent?.split(/\((.*?)\)/)[1] || '';

      // prepare all booking data first
      const bookingData2 = roomCounts.map((room, idx) => ({
        adults: room.adults,
        children: room.children,
        pets: room.pets,
        rooms: room.rooms,
        arrivalDate,
        departureDate,
        isCorporate: 0,
        ratePlanCodes: selectedRooms[idx].ratePlanCode,
        roomtype: selectedRooms[idx].code,
        roomTypeName: selectedRooms[idx].name,
        ratePlanCodeInDigit: ratePlanCode || '',
        corpCode: corpCode || '',
      }));

      // pick only one by index
      const singleBookingData = bookingData2[index];
      if (!singleBookingData) {
        throw rejectWithValue({
          paymentResData: {},
          status: {
            apiMessage: `No booking data found for index ${index}`,
            apiSuccess: false,
          },
        });
      }

      dispatch(setBookingData([singleBookingData]));

      const { costSummary, signIn } = getState();
      const { guestInfo, paymentMethod, paymentMode } = costSummary;
      const { userData } = signIn;
      const hotelId = hotelDetails?.hotelId;

      // compute single-room total (room cost + selected add-ons [+ pet fee if none selected] + taxes)
      const currentRoom = selectedRooms[index] || {};
      const currentRoomCounts = roomCounts[index] || {};
      const optionsForRoom = (allOptions2 && allOptions2[index]) || [];
      let lineItems = [];
      // Room base
      lineItems.push({ amt: Number(currentRoom?.amtTotal || 0), taxes: Array.isArray(currentRoom?.taxes) ? currentRoom.taxes : [] });
      // Selected add-ons
      optionsForRoom.forEach(option => {
        (option?.options || []).forEach(opt => {
          if (opt?.isSelected) {
            if (opt?.type === 'pet') {
              const amt = Number(opt?.amtTotal || 0) * Number(queryDetails?.pets || 0);
              lineItems.push({ amt, taxes: Array.isArray(opt?.taxes) ? opt.taxes : [] });
            } else if (opt?.isCostPerDay) {
              const amt = Number(opt?.amtTotal || 0);
              lineItems.push({ amt, taxes: Array.isArray(opt?.taxes) ? opt.taxes : [] });
            } else {
              const amt = Number(opt?.amount || 0);
              lineItems.push({ amt, taxes: Array.isArray(opt?.taxes) ? opt.taxes : [] });
            }
          }
        });
      });
      // Pet fee fallback if pets > 0 and no pet add-on selected
      const hasPetAddon = optionsForRoom.some(option => (option?.options || []).some(opt => opt?.isSelected && opt?.type === 'pet'));
      if (!hasPetAddon && Number(currentRoomCounts?.pets || 0) > 0) {
        const petChargePer = Number(currentRoom?.petCharge || 0);
        const amt = petChargePer * Number(currentRoomCounts?.pets || 0);
        if (amt > 0) lineItems.push({ amt, taxes: [] });
      }
      // Sum amounts and taxes
      const subtotal = lineItems.reduce((acc, li) => acc + Number(li.amt || 0), 0);
      const taxes = lineItems.flatMap(li => Array.isArray(li.taxes) ? li.taxes : []);
      const totalTax = taxes.reduce((acc, t) => acc + Number(t?.taxAmt || 0), 0);
      const singleTotalAmount = Number((subtotal + totalTax).toFixed(2));
      // put per-room total into tokenized data
      dataCopy.amount = singleTotalAmount;

      const value = {
        bookingData: [singleBookingData], // only this room’s data
        data: dataCopy,
        emailId: userData?.email || guestInfo?.email || dataCopy?.email,
        hotelId,
        IPaddress: IpAddress?.data?.ip || null,
        isAdvancePurchase: false,
        machineName: 'web',
        OS: userOs,
        paymentMethod,
        paymentMode,
        saveCardForLaterUse: false,
      };

      const res = await AXIOS_RESERVATION.post('/paymentv3', value);

      if (res?.data?.success) {
        // pass index forward so reservation knows which room
        dispatch(makeReservation({ ...res?.data?.result, queryDetails, index, singleTotalAmount, uniqueDate }));
        return {
          paymentResData: res?.data?.result,
          status: {
            apiMessage: res?.data?.result?.message,
            apiSuccess: res?.data?.success,
          },
        };
      }
    } catch (error) {
      console.log(error, 'issue...');
      return rejectWithValue({
        paymentResData: {},
        status: {
          apiMessage: error?.response?.data?.result?.message || 'Payment failed',
          apiSuccess: error?.response?.data?.success || false,
        },
      });
    }
  },
);

/**
 * Step 4 - Reservation (room-specific)
 */
export const makeReservation = createAsyncThunk(
  'costSummary/makeReservation',
  async (paymentResData, { getState, rejectWithValue }) => {
    try {
      console.log('makeReservation>>>>>>>');

      const {
        costSummary, room, calender, signIn, stripe,
      } = getState();
      const { guestInfo, totalCost, paymentMode, additionalGuests, connectingRoomsPreferred } = costSummary;
      const { selectedRooms, allOptions2, roomCounts } = room;
      const { hotelDetails } = stripe;
      const hotelId = hotelDetails?.hotelId;
      const { userData } = signIn;

      const {
        _id, roomData, accessToken, paymentMethod, resTransactionId,
        queryDetails, encUserCardData, index, singleTotalAmount, uniqueDate,
      } = paymentResData;

      const { adults, children, pets, rooms } = queryDetails;
      const connectingRoomsPreferredFlag = Boolean(connectingRoomsPreferred) && roomCounts?.length > 1;
      const {
        firstName, lastName, email, countryCodeForMobileNumber,
        mobileNoWithoutCountryCode, phoneNo,
      } = guestInfo;
      const { arrivalDate, departureDate } = calender;

      // ✅ current room only
      const currentRoom = selectedRooms[index];
      const currentRoomCounts = roomCounts[index];

      const roomDataNow = [{
        totalRoomAmount: currentRoom.amtTotal,
        totalRoomPrice: currentRoom.avgRate,
        roomTypeName: currentRoom.name,
        roomTypeCode: currentRoom.code,
        ratePlanCode: currentRoom.ratePlanCode,
        numberOfAdults: currentRoomCounts.adults,
        numberOfChildren: currentRoomCounts.children,
        numberOfPets: currentRoomCounts.pets,
        numberOfRooms: currentRoomCounts.rooms,
      }];

      // ✅ AddOns only for this room
      const addOnsForRoom = [];
      allOptions2?.[index]?.forEach(option => {
        option.options.forEach(opt => {
          if (opt.isSelected) {
            addOnsForRoom.push(opt);
          }
        });
      });

      if (currentRoom?.addOn) {
        addOnsForRoom.push(currentRoom.addOn);
      }

      const IpAddress = await axios.get('https://api.ipify.org?format=json');
      const { userAgent } = window.navigator;
      const userOs = userAgent?.split(/\((.*?)\)/)[1] || '';

      // Build optional additional guest for this reservation index (skip first)
      const addGuest = index > 0 ? additionalGuests?.[index] : null;
      const additionalGuest = addGuest && (
        addGuest.firstName || addGuest.lastName || addGuest.email || addGuest.mobileNoWithoutCountryCode
      ) ? addGuest : undefined;
      const isFirstReservation = index === 0 && roomCounts?.length > 1;

      const value = {
        _id,
        accessToken,
        addOns: addOnsForRoom,      // only this room’s addOns
        // addOns2: [addOnsForRoom],   // normalized array
        // roomDataNew: roomDataNow,
        arrivalDate,
        departureDate,
        emailId: userData?.email || email,
        hotelId,
        IPaddress: IpAddress?.data?.ip || null,
        isLoyaltyApply: userData?.isLoyaltyApply || false,
        machineName: 'web',
        numberOfAdults: roomDataNow[0]?.numberOfAdults || adults,
        numberOfChildren: roomDataNow[0]?.numberOfChildren || children,
        numberOfPets: roomDataNow[0]?.numberOfPets || pets,
        OS: userOs,
        paymentMethod,
        paymentType: paymentMode,
        primaryGuest: {
          addresses: [
            {
              addressLines: ['tutti sign', ''],
              city: 'Digital',
              country: 'United States',
              countryCode: 'US',
              postalCode: '2388',
              state: 'Colorado',
              stateProvinceCode: 'CO',
              type: 'Home',
            },
          ],
          countryCodeForMobileNumber:
            userData?.countryCodeForMobileNumber || countryCodeForMobileNumber,
          firstName: userData?.firstName || firstName,
          lastName: userData?.lastName || lastName,
          mobileNoWithoutCountryCode:
            userData?.mobileNoWithoutCountryCode || mobileNoWithoutCountryCode,
          phoneNo: userData?.phoneNo || phoneNo,
          stayProfileId: '',
        },
        roomData,
        rooms,
        resTransactionId,
        specialRequest: '',
        ratePlanCode: currentRoom.ratePlanCode,
        roomTypeCode: currentRoom.code,
        roomTypeName: currentRoom.name,
        totalRoomAmount: currentRoom.amtTotal,
        totalRoomPrice: currentRoom.avgRate,
        totalAmount: Number(singleTotalAmount || 0),
        aORbTest: localStorage.getItem('PUSHTELL-bookingEngine') || "",
        connectingRoomsPreferred: connectingRoomsPreferredFlag,
        clubReservationCode: uniqueDate,
        ...(additionalGuest ? { additionalGuest } : {}),
        encUserCardData,
        isFirst:isFirstReservation
      };

      const res = await AXIOS_RESERVATION.post('/v1/reservations', value);

      if (res?.data?.success) {
        return {
          reservationRespData: res?.data?.result,
          index,
          status: {
            apiMessage: res?.data?.result?.message,
            apiSuccess: true,
          },
        };
      }
      throw {
        status: {
          apiMessage: res?.data?.result?.message
            || 'Something went wrong please try again later',
          apiSuccess: false,
        },
      };
    } catch (error) {
      throw rejectWithValue(error);
    }
  },
);
